import React, { useState } from 'react'
import useTrans from '@hooks/useTrans'
import { cn, dateFormat, getUserType } from '@utils/safeTs'
import type { AnagraficaAziendaType, FornitoreType, OrdineType, RootStateType, StatoOrdineChoices, UtenteType } from '@utils/types'
import MyAccountOrdineDetailBox from '../MyAccountOrdineDetailBox/MyAccountOrdineDetailBox'
import Avatar from '@components/atoms/Avatar/Avatar'
import StatoOrdine from '@components/atoms/StatoOrdine/StatoOrdine'
import styles from './MyAccountOrdineDetailInfoOrdine.module.sass'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import IncaricatoDropdown from '@components/atoms/IncaricatoDropdown/IncaricatoDropdown'
import { api } from '@utils/index'
import { ORDINI } from '@utils/endpoint'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import { Loading } from '@components/atoms'
import AvatarGroup from '../AvatarGroup/AvatarGroup'

type ExtendedOrdineType = OrdineType & {
  commerciali?: UtenteType[]
  riferimentoCliente?: string
}

export interface MyAccountOrdineDetailInfoOrdineProps {
  ordine?: ExtendedOrdineType
  setOrdine?: any
  className?: string
}

const MyAccountOrdineDetailInfoOrdine = ({
  ordine,
  setOrdine,
  className,
}: MyAccountOrdineDetailInfoOrdineProps) => {

  const t = useTrans()
  const { utente } = useSelector((state: RootStateType) => state.utente)
  const userType = getUserType(utente)
  const [savingIncaricato, setSavingIncaricato] = useState(false)

  const getOrdini = (ordine) => ordine?.isParent ? ordine.ordini || [] : ordine ? [ordine] : []

  const fornitoriIncaricati: {
    key: string
    fornitore: FornitoreType
    incaricato: UtenteType
  }[] = _.uniqBy(
    getOrdini(ordine).flatMap((o) => {
      if (o?.fornitore) {
        return [{
          key: `fornitore-${o.fornitore.id}`,
          fornitore: o.fornitore,
          incaricato: o.incaricato,
        }]
      }

      // Se fornitore non è sull'ordine allora l'ordine non è ancora stato splittato/assegnato ad un fornitore
      // considero quindi i fornitori delle personalizzazioni dell'ordine, se ce ne sono più di uno allora considero un fornitore fittizio "multifornitore"
      const fornitoriPersonalizzazioni = _.uniqBy(
        o?.personalizzazioniOrdine?.map(personalizzazione => personalizzazione.fornitore).filter(Boolean) || [],
        fornitore => fornitore.id
      )

      if (fornitoriPersonalizzazioni.length > 1) {
        return [{
          key: `multifornitore-${o?.id ?? 'ordine'}`,
          fornitore: {
            id: -(o?.id || 1),
            ragioneSociale: t("Multifornitore: ordine non ancora splittato"),
          },
          incaricato: undefined,
        }]
      }

      return fornitoriPersonalizzazioni.map(fornitore => ({
        key: `fornitore-${fornitore.id}`,
        fornitore,
        incaricato: undefined,
      }))
    }),
    (entry) => entry.key
  )

  const getFullName = (utente?: UtenteType) =>
    [utente?.nome, utente?.cognome].filter(Boolean).join(' ').trim()

  const handleChangeIncaricato = (incaricato) => {
    getOrdini(ordine).forEach((o) => {
      setSavingIncaricato(true)
      api
        .put(`${ORDINI}${o.id}/`, { incaricatoId: incaricato.id })
        .then(() => {
          setOrdine(op => (op.isParent 
            ? {...op, ordini: op.ordini.map(o2 => o2.id === o.id 
              ? ({...o2, incaricato: incaricato }) 
              : o2
              )}
            : op.id === o.id ? {...op, incaricato: incaricato } : op
          ))
          displayNotification('changeincaricato_' + ordine.id + "_success", t("Incaricato salvato con successo"), "success")
        })
        .catch((response) => {
          console.log(response)
          displayNotification('changeincaricato_' + ordine.id + "_error", t("Errore durante il salvataggio dell'incaricato"), "error")
        })
        .finally(() => {
          setSavingIncaricato(false)
        })
    })  
  }

  const renderPlaceholder = () => <span className={styles.placeholder}>-</span>

  const renderIncaricato = () => {
    if (!fornitoriIncaricati?.length) {
      return renderPlaceholder()
    }

    return userType === 'fornitore' ? (
      // se userType è fornitore - sicuro c'è solo un fornitoreIncaricato
      <div className={styles.incaricato}>
        {savingIncaricato ? (<Loading width="20" height="20" />) : (
          <>
            <IncaricatoDropdown 
              value={fornitoriIncaricati?.[0]?.incaricato?.id}
              options={fornitoriIncaricati?.[0]?.fornitore.utenti} 
              onChange={handleChangeIncaricato}
            />
            {!!fornitoriIncaricati?.length && <span className={styles.contactName}>{getFullName(fornitoriIncaricati[0].incaricato)}</span>}
          </>
        )}
      </div>
    ) : (
      <span className={styles.incaricatoList}>
        {_.uniqBy(fornitoriIncaricati.filter(x => !!x.incaricato), (x) => x.incaricato.id)?.length > 1 ? (
          <AvatarGroup id={'incaricati'} utenti={_.uniqBy(fornitoriIncaricati.filter(x => !!x.incaricato), (x) => x.incaricato.id).map(x => x.incaricato)} />
        ) : (
          _.uniqBy(fornitoriIncaricati.filter(x => !!x.incaricato), (x) => x.incaricato.id)
          .map((x) => renderUser(x.incaricato, `incaricato-${x.incaricato.id}`))
        )}
      </span>
    )
  }

  const renderUser = (user, id) => {
    if (!user)
      return renderPlaceholder()

    const name = getFullName(user)

    return (
      <div className={styles.contact} key={`${id}-${user.id}`}>
        <Avatar
          id={`${id}-avatar`}
          utente={user}
        />
        <span className={styles.contactName}>{name}</span>
      </div>
    )
  }

  const renderCommerciale = () => {
    const commerciali = getOrdini(ordine)?.map(o => o.commerciale)?.filter(c => !!c)

    if (!commerciali.length) {
      return renderPlaceholder()
    }

    const [first] = commerciali
    
    return renderUser(first, `${ordine?.numeroProtocollo ?? 'ordine'}-commerciale`)
  }

  const renderStato = () =>
    ordine?.statoOrdineCalcolato ? (
      <div className={styles.stato}>
        {_.uniq(getOrdini(ordine).map(o => o.statoOrdineCalcolato)).map(v => 
          <StatoOrdine stato={v as StatoOrdineChoices} key={v} />
        )}
      </div>
    ) : (
      renderPlaceholder()
    )

  const data = [
    { 
      key: 'fornitori', 
      label: t(fornitoriIncaricati.length > 1 ? "Fornitori" : "Fornitore"),
      value: fornitoriIncaricati.map(x => x.fornitore?.ragioneSociale).join(", ")
    },
    { 
      key: 'incaricati', 
      label: t(fornitoriIncaricati.length > 1 ? "Incaricati" : "Incaricato"), 
      value: renderIncaricato(),
      align: 'center',
    },
    { key: 'commerciale', label: t("Commerciale"), value: renderCommerciale() || '-', align: 'center' },
    { key: 'stato', label: t("Stato ordine"), value: renderStato() },
    { key: 'created', label: t("Data creazione ordine"), value: dateFormat(ordine.created) },
    { key: 'numeroDocumento', label: t("Numero documento"), value: _.uniq(getOrdini(ordine).map(o => `${o.numeroDocumento}${o.ordineFornitore ? ` (${o.ordineFornitore.numeroDocumento})` : ''}`).filter(r => !!r)).map((r: string, idx) => <React.Fragment key={idx}>{idx > 0 ? <br /> : <></>}{r}</React.Fragment>) },
    { key: 'riflavorazione', label: t("Riferimento lavorazione"), value: _.uniq(getOrdini(ordine).map(o => o.riferimentoLavorazione).filter(r => !!r)).map((r: string, idx) => <React.Fragment key={idx}>{idx > 0 ? <br /> : <></>}{r}</React.Fragment>) },
    { key: 'rifcliente', label: t("Riferimento cliente"), value: _.uniq(getOrdini(ordine).map(o => o.riferimentoCliente).filter(r => !!r)).map((r: string, idx) => <React.Fragment key={idx}>{idx > 0 ? <br /> : <></>}{r}</React.Fragment>) },
    ...getOrdini(ordine).filter(o => o.ristampa && !!o.noteRistampa)?.length 
      ? [{ key: 'note_ristampa', label: t("Note ristampa"), value: _.uniq(getOrdini(ordine).map(o => o.noteRistampa).filter(r => !!r)).map((r: string, idx) => <React.Fragment key={idx}>{idx > 0 ? <br /> : <></>}{r}</React.Fragment>) }]
      : []
  ]

  return (
    <MyAccountOrdineDetailBox
      title={t('Informazioni ordine')}
      className={cn(styles.root, className)}
      bodyClassName={styles.list}
    >
      {data.map(x => (
        <div className={cn(styles.item, x.align ? styles[`item--${x.align}`] : '')} key={x.key}>
          <div className={styles.label}>{x.label}:</div>
          <div className={styles.value}>{x.value}</div>
        </div>
      ))}
    </MyAccountOrdineDetailBox>
  )
}

export default MyAccountOrdineDetailInfoOrdine
